import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { initials } from '../lib/format';

import type { RecordStatus } from '../lib/types';

/** Picks the most specific translated message for an API error */
function errorKey(e: { code?: string; message?: string }) {
  if (e.code === 'validation' && /phone/i.test(e.message ?? '')) return 'errors.invalid_phone';
  if (e.code === 'validation' && /time zone/i.test(e.message ?? '')) return 'errors.invalid_timezone';
  return e.code ? `errors.${e.code}` : '';
}

export function Photo({ src, name, size = 48 }: { src: string | null | undefined; name: string; size?: number }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="photo" style={{ width: size, height: size, fontSize: size * 0.36 }} aria-hidden={!src}>
      {src && !broken ? <img src={src} alt={name} onError={() => setBroken(true)} /> : initials(name)}
    </span>
  );
}

export function StatusBadge({ status, alert }: { status: RecordStatus; alert?: boolean }) {
  const { t } = useTranslation();
  return <span className={`badge status-${status}${alert ? ' is-alert' : ''}`}>{t(`status.${status}`)}</span>;
}

export function Modal({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className={`modal${wide ? ' modal-wide' : ''}`} onClose={onClose} onCancel={onClose}>
      {open && (
        <>
          <header className="modal-head">
            <h2>{title}</h2>
            <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
              ✕
            </button>
          </header>
          <div className="modal-body">{children}</div>
        </>
      )}
    </dialog>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const e = error as { code?: string; message?: string };
  const key = errorKey(e);
  const msg = key && t(key, { defaultValue: '' }) ? t(key) : e.message || t('errors.generic');
  return (
    <p className="error-note" role="alert">
      {msg}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

type Toast = { id: number; text: string; kind: 'ok' | 'err' };
let push: ((t: Omit<Toast, 'id'>) => void) | null = null;
export const toast = (text: string, kind: Toast['kind'] = 'ok') => push?.({ text, kind });

export function Toaster() {
  const [items, setItems] = useState<Toast[]>([]);
  useEffect(() => {
    push = (t) => {
      const id = Date.now() + Math.random();
      setItems((x) => [...x, { ...t, id }]);
      setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), 4000);
    };
    return () => {
      push = null;
    };
  }, []);
  return (
    <div className="toaster" aria-live="polite">
      {items.map((i) => (
        <div key={i.id} className={`toast toast-${i.kind}`}>
          {i.text}
        </div>
      ))}
    </div>
  );
}

/** Wraps an async action: disables while running and surfaces errors as toasts */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation();
  const run = async <T,>(fn: () => Promise<T>, okText?: string): Promise<T | undefined> => {
    setBusy(true);
    try {
      const r = await fn();
      if (okText) toast(okText);
      return r;
    } catch (e) {
      const err = e as { code?: string; message?: string };
      const key = errorKey(err);
      toast(key && t(key, { defaultValue: '' }) ? t(key) : err.message || t('errors.generic'), 'err');
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

export function LanguageSwitch({ onChange }: { onChange?: (l: 'en' | 'ar') => void }) {
  const { i18n } = useTranslation();
  const next = i18n.language === 'ar' ? 'en' : 'ar';
  return (
    <button
      type="button"
      className="lang-switch"
      lang={next}
      onClick={() => (onChange ? onChange(next) : void i18n.changeLanguage(next))}
    >
      {next === 'ar' ? 'العربية' : 'English'}
    </button>
  );
}
